import React from 'react';
import propTypes from 'prop-types';
import { Form } from 'shared-ui';
import { isDefined, isValidDate, formatDate, isBoolean } from '@dateam/ark';
import { concatClassName } from '@dateam/ark-react';
import { validate as validator, ValidationError } from 'data/observation';
import { ObservationProps, plotObservationPropTypes } from './types';
import { ObservationErrors } from './components';
import ObservationField from './ObservationField';
import ObservationItem from './ObservationItem';
import styles from './Observation.module.scss';

const observationKey = 'observation35';

type Props = ObservationProps<App.Observation35>;

const getPresenceLabel = (presence: boolean | null) => {
    if (isBoolean(presence)) return presence ? 'Oui' : 'Non';

    return '-';
};

const Observation35: React.FC<Props> = ({
    data,
    plot,
    ignorable,
    className,
    onChange
}: Props) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, ValidationError>>({});
    const internalData = React.useRef(data);

    React.useEffect(() => {
        internalData.current = data;
    }, [data]);

    React.useEffect(() => {
        if (data.presence === undefined &&
            data.count1 === undefined &&
            data.count2 === undefined) {
            return;
        }

        setValidationErrors(validator(data, plot));
    }, [plot, data]);

    const presence = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.presence ?? undefined;
    }, [data]);

    const count1 = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.count1 ?? undefined;
    }, [data]);

    const count2 = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.count2 ?? undefined;
    }, [data]);

    const previousValue = React.useMemo(() => {
        if (isDefined(internalData.current.previous)) {
            return (
                <>
                    {isValidDate(internalData.current.previous.updatedOn) && (
                        <div className={styles['previousDate']}>{formatDate(internalData.current.previous.updatedOn)}</div>
                    )}
                    <div className={styles['previous']}>Oui/Non: {getPresenceLabel(internalData.current.previous.presence?.value ?? null)}</div>
                    <div className={styles['previous']}>Comptage: {internalData.current.previous.count1?.value ?? '-'}</div>
                    <div className={styles['previous']}>Comptage: {internalData.current.previous.count2?.value ?? '-'}</div>
                </>
            );
        }

        return (<div>Aucune valeur disponible</div>);
    }, [internalData.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIgnoreObservation = React.useCallback(() => {
        const newData: App.Observation35 = {
            ...internalData.current,
            presence: null,
            count1: null,
            count2: null,
            ignored: !internalData.current.ignored
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handlePresenceChange = React.useCallback((newValue: boolean | undefined) => {
        const newData: App.Observation35 = {
            ...internalData.current,
            presence: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleCount1Change = React.useCallback((newValue: number | undefined) => {
        const newData: App.Observation35 = {
            ...internalData.current,
            count1: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleCount2Change = React.useCallback((newValue: number | undefined) => {
        const newData: App.Observation35 = {
            ...internalData.current,
            count2: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    return (
        <ObservationItem
            data={data}
            ignorable={ignorable}
            onIgnore={handleIgnoreObservation}
            previous={previousValue}
            label="Gribouri / Ecrivain"
        >
            <div className="row">
                <ObservationField
                    id={`${observationKey}-presence`}
                    label="Oui/Non"
                    className={concatClassName(styles[`${observationKey}-presence`], 'col-4')}
                >
                    <Form.Select
                        selected={presence}
                        onChange={handlePresenceChange}
                        state={validationErrors['presence']?.type}
                        readOnly={data.ignored}
                    >
                        <Form.Select.Option value={null} />
                        <Form.Select.Option value={true}>Oui</Form.Select.Option>
                        <Form.Select.Option value={false}>Non</Form.Select.Option>
                    </Form.Select>
                </ObservationField>
                <ObservationField
                    id={`${observationKey}-count1`}
                    label="Comptage"
                    offLabel="/25 ceps"
                    className={concatClassName(styles[`${observationKey}-count1`], 'col-4')}
                >
                    <Form.Number
                        min={0}
                        max={25}
                        value={count1}
                        onChange={handleCount1Change}
                        state={validationErrors['count1']?.type}
                        readOnly={data.ignored}
                    />
                </ObservationField>
                <ObservationField
                    id={`${observationKey}-count2`}
                    label="Comptage"
                    offLabel="/25 grappes"
                    className={concatClassName(styles[`${observationKey}-count2`], 'col-4')}
                >
                    <Form.Number
                        min={0}
                        max={25}
                        value={count2}
                        onChange={handleCount2Change}
                        state={validationErrors['count2']?.type}
                        readOnly={data.ignored}
                    />
                </ObservationField>
            </div>
            <ObservationErrors value={validationErrors} observationKey={observationKey} />
        </ObservationItem>
    );
};

Observation35.propTypes = {
    data: propTypes.any.isRequired,
    plot: plotObservationPropTypes.isRequired,
    className: propTypes.string,
    onChange: propTypes.func
};

Observation35.defaultProps = {
    className: undefined,
    onChange: undefined
};

export default Observation35;
